import { createAsyncThunk } from "@reduxjs/toolkit"
import axios, { AxiosResponse } from "axios"
import { setTokenAction } from "../auth/slice"
import { getAllCentersListAction, resetCentreProfilePicPathAction, setAllBlockedCandidateListAction, setAllCandidateListAction, setAllCentreUserListAction, setAllPreferredCandidateListAction, setCandidateNotificationSettings, setCentreDetailsAction, setCentrePicAction, setCentreProfilePicPathAction, setTotalCentreUsersCountAction } from "./slice"
import { CentreDetailsPayload, IClientSaveSettingsInput, TblockUnblockCandidateResponse, TCenterUserInactiveResponse, TClientNotifiationSettingsResponse, TCreateCenterUserResponse, TCreateCenterUserValues, TGetAllCentreResponse, TGetCentreUsersResponse, TUpdateCenterUserResponse, TUpdateCentreSuccessResponse } from "./types"
import { TProfileImageResponse, TProfilePicValues } from "../auth/types"
import { TGetAllCandidatesResponse } from "../centers/types"
import { TBlockUnBlockCandidatePayload } from "../../models/vacancy"
import { ICandidateResponse } from "../superadmin/types"
import { setCandidateReviewDataAction } from "../superadmin/slice"

/**
 * POST method
 * createCenterUsersThunk - Submit the form to create the center users
 */
export const createCenterUsersThunk = createAsyncThunk(
    'centers/createCenterUsersThunk',
    async (data: TCreateCenterUserValues, { dispatch, rejectWithValue }) => {
      try {
        const response: AxiosResponse<TCreateCenterUserResponse> = await axios.post(`/v1/centres/users`, data)
        await dispatch(setTokenAction(response.data.data.token))
        return response.data
      } catch (err) {
        return rejectWithValue((err as any).response.data || err)
      }
    }
)

/**
 * PUT method
 * UpdateCenterUsersThunk - Submit the form to create the center users
 */
export const updateCenterUsersThunk = createAsyncThunk(
  'centers/updateCenterUsersThunk',
  async (data: TCreateCenterUserValues, { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<TUpdateCenterUserResponse> = await axios.put(`/v1/centres/users/${data.id}`, data)
      await dispatch(setTokenAction(response.data.data.token))
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * GET method
 * getAllCenters - To get all the centers list
 */
export const getAllCenters = createAsyncThunk(    
  'centers/getAllCenters',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<TGetAllCentreResponse> = await axios.get(`/v1/centres`)
      await dispatch(setTokenAction(response.data.data.token))
      await dispatch(getAllCentersListAction(response.data.data.centres))
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * GET method
 * getAllCentreUsers - To get all the centers users list
 */
export const getAllCentreUsers = createAsyncThunk(    
  'centers/getAllCentreUsers',
  async (
    { page, keyword = '', centre_id, role_id, per_page }: 
    { page: number; keyword?: string; centre_id?: string | number | null | Array<number>; role_id?: string | number | null | Array<number>, per_page: number }, 
    { rejectWithValue, dispatch }
  ) => {
    try {
      // Convert centre_id and role_id to comma-separated strings if they are arrays
      const centreIdParam = Array.isArray(centre_id) ? centre_id?.length > 0 ?'['+centre_id.join(',')+']' : '' : centre_id;
      const roleIdParam = Array.isArray(role_id) ? role_id?.length > 0 ? '['+role_id.join(',')+']' : '' : role_id;

      const response: AxiosResponse<TGetCentreUsersResponse> = await axios.get(`/v1/centres/users`, {
        params: { page, keyword, centre_id: centreIdParam, role_id: roleIdParam, per_page: per_page }
      });

      await dispatch(setTokenAction(response.data.data.token));
      await dispatch(setAllCentreUserListAction(response.data.data.users));
      await dispatch(setTotalCentreUsersCountAction(response.data.data.users.total))
      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

/**
 * POST method
 * uploadsCentrePictureThunk - Upload the profile picture
 */
export const uploadsCentrePictureThunk = createAsyncThunk(
  'app/uploadsCentrePictureThunk',
  async (data: FormData | TProfilePicValues, { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<TProfileImageResponse> = await axios.post(`/v1/centres/profile-image`, data)
      await dispatch(setTokenAction(response.data.data.token))
      await dispatch(resetCentreProfilePicPathAction())
      await dispatch(setCentrePicAction(response.data.data.profile_picture))
      await dispatch(setCentreProfilePicPathAction(response.data.data.photo_path))
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * PUT method
 * updateCentreDetails - Update the centre details
 */
export const updateCentreDetails = createAsyncThunk(
  'centers/updateCentreDetails',
  async (data: CentreDetailsPayload, { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<TUpdateCentreSuccessResponse> = await axios.put(`/v1/centres/${data.id}`, data)
      await dispatch(setTokenAction(response.data.data.token))
      await dispatch(setCentreDetailsAction(response.data.data.centres))
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)


/**
 * PUT method
 * updateCentreStatus - Update the centre status
 */
export const updateCentreStatus = createAsyncThunk(
  'centers/updateCentreStatus',
  async (data: FormData, { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<any> = await axios.put(`/v1/centres/activate`, data)
      await dispatch(setTokenAction(response.data.data.token))
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * POST method
 * CenterUserInactiveThunk - To deactivate the centre user
 */
export const CenterUserInactiveThunk = createAsyncThunk(
  'centers/CenterUserInactiveThunk',
  async (data: {user_id: number}, { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<TCenterUserInactiveResponse> = await axios.post(`/v1/centres/user/deactivate`, data)
      await dispatch(setTokenAction(response.data.data.token))
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * GET method
 * getAllCandidatesThunk - To get all the preferred, active, and blocked candidates list
 */

export const getAllCandidatesThunk = createAsyncThunk(    
  'centers/getAllCandidatesThunk',
  async (
    { search_from = '', keyword = '', preferred_page, approved_page, blocked_page, preferred_per_page, approved_per_page, blocked_per_page, preferred_sort_by="first_name", preferred_sort_order = "asc", approved_sort_by="first_name", approved_sort_order = "asc", blocked_sort_by="first_name", blocked_sort_order="asc", selected_positions_levels, centre_uuid, worked_with_previously= false, distance_away= 10 }: 
    { search_from?: string; keyword?: string; preferred_page?: string | number | null | Array<number>; approved_page?: string | number | null | Array<number>, blocked_page?: string | number | null | Array<number>,preferred_per_page: number, approved_per_page: number; blocked_per_page: number; preferred_sort_by: string; preferred_sort_order: string; approved_sort_by: string; approved_sort_order: string; blocked_sort_by: string; blocked_sort_order: string, selected_positions_levels?: string | number | null | Array<number>, centre_uuid: string | null, worked_with_previously: boolean, distance_away: string | number | null }, 
    { rejectWithValue, dispatch }
  ) => {
    try {
      const PositionsLevelsParam = Array.isArray(selected_positions_levels) ? selected_positions_levels?.length > 0 ?'['+selected_positions_levels.join(',')+']' : '' : selected_positions_levels;
      const response: AxiosResponse<TGetAllCandidatesResponse> = await axios.get(`/v1/centres/candidates`, {
        params: { search_from, keyword, preferred_page: preferred_page, approved_page: approved_page, blocked_page: blocked_page, preferred_per_page: preferred_per_page, approved_per_page: approved_per_page, blocked_per_page: blocked_per_page, preferred_sort_by: preferred_sort_by, preferred_sort_order: preferred_sort_order, approved_sort_by: approved_sort_by, approved_sort_order: approved_sort_order, blocked_sort_by: blocked_sort_by, blocked_sort_order: blocked_sort_order, position_ids: PositionsLevelsParam, centre_uuid: centre_uuid, worked_with_previously: worked_with_previously, distance_away: distance_away }
      });
      await dispatch(setTokenAction(response.data.data.token));
      await dispatch(setAllCandidateListAction(response.data.data.candidates.total_candidates.candidates));
      await dispatch(setAllBlockedCandidateListAction(response.data.data.candidates.blocked_candidates.blocked_candidates));
      await dispatch(setAllPreferredCandidateListAction(response.data.data.candidates.preferred_candidates.preferred_candidates));
      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

/**
 * POST method
 * blockUnblockCandidateThunk - Block or Unblock the candidate
 */
export const blockUnblockCandidateThunk = createAsyncThunk(
  'centers/blockUnblockCandidateThunk',
  async (data: TBlockUnBlockCandidatePayload, { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<TblockUnblockCandidateResponse> = await axios.post(`/v1/centres/users/block`, data)
      await dispatch(setTokenAction(response.data.data.token))
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * GET method
 * getCandidateDataForCentreThunk - Getting candidate data for review popup
 */
export const getCandidateDataForCentreThunk = createAsyncThunk(
  'superadmin/getCandidateDataForCentreThunk',
  async (data: { candidate_id: string | undefined, centre_id: string | null }, { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<ICandidateResponse> = await axios.get(`/v1/admin/candidates/${data?.candidate_id}?basic_details=true&centre_id=${data.centre_id}`)
      await dispatch(setTokenAction(response.data.data.token))
      await dispatch(setCandidateReviewDataAction(response.data.data?.[0]))
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const submitClientNotificationSettings = createAsyncThunk(
  'candidates/submitClientNotificationSettings',
  async (data: IClientSaveSettingsInput, { rejectWithValue, dispatch }) => {
    try {
      const responseAvailabilities: AxiosResponse<any> = await axios.post('/v1/notification-settings', data)
      await dispatch(setTokenAction(responseAvailabilities.data.data.token))
      return responseAvailabilities.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const getClientSettingsDataThunk = createAsyncThunk(    
  'candidates/getCandidateSettingsDataThunk',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<TClientNotifiationSettingsResponse> = await axios.get(`/v1/notification-settings`)
      await dispatch(setTokenAction(response.data.data.token))

      if(response.data.data.user_settings){
        await dispatch(setCandidateNotificationSettings(response.data.data?.user_settings))
      }      
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)